import type {AgendaEventAppointment} from '@/features/agenda/types'
import {
    StyledAgendaScheduleEvent,
    StyledAgendaScheduleEventName,
    StyledAgendaScheduleEventSubject
} from '@/features/agenda/components/agenda-schedule-event/style'
import {eventTypeMapToLabel} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import {useAgendaNavigation} from '@/features/agenda/hooks/useAgendaNavigation'
import {useMemo} from 'react'
import {DialogDisclosure, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {AgendaEventDetailsDialog} from '@/features/agenda/components/agenda-event-details-dialog/AgendaEventDetailsDialog'

interface AgendaScheduleEventProps {
    event: AgendaEventAppointment
}

export const AgendaScheduleEvent = ({event}: AgendaScheduleEventProps) => {
    const {t} = useTranslation()
    const {
        params: {entity}
    } = useAgendaNavigation()

    const name = useMemo(() => {
        if (entity == 'patients') {
            return event?.category == 'team_meeting'
                ? event?.eventTeams?.map(team => team.member?.fullName).join(', ')
                : event?.eventConsultants?.map(consultant => consultant.user?.fullName).join(', ')
        }

        if (entity == 'users') {
            return event?.eventPatients?.map(patient => patient.patient?.fullName).join(', ')
        }
    }, [entity, event])

    const eventName = useMemo(() => {
        return event.eventConsultants
            ?.map(consultant => t(eventTypeMapToLabel[consultant.accessType.accessType]))
            .join(', ')
    }, [event])

    return (
        <DialogProvider>
            <DialogDisclosure
                disabled={event.status == 'tentative'}
                render={
                    <StyledAgendaScheduleEvent align="center" gap={1}>
                        {event.category ? (
                            <StyledAgendaScheduleEventName $isTentative={event.status == 'tentative'}>
                                {eventName}
                            </StyledAgendaScheduleEventName>
                        ) : (
                            '-'
                        )}
                        <StyledAgendaScheduleEventSubject>
                            {t('agenda:with_subject', {
                                subject: name
                            })}
                        </StyledAgendaScheduleEventSubject>
                    </StyledAgendaScheduleEvent>
                }
            />
            <AgendaEventDetailsDialog eventId={event.id} />
        </DialogProvider>
    )
}
